import React, { useEffect, useRef } from 'react';
import Popover from './Popover';
import Button from './Button';
import Icon from './Icon';
import { ICONS } from './icon-map';
import './ds.css';
import './DataTable/datatable.css';

/*
 * ActionMenu: groups related toolbar actions behind one button.
 *
 *   <ActionMenu
 *     label="Sync"
 *     icon={ICONS.refresh}
 *     items={[
 *       { label: 'Refresh database', icon: ICONS.database, onClick: refreshDb },
 *       { label: 'Fill monthly from Ejar', description: '105 contracts', onClick: fill, disabled: busy },
 *       { divider: true },
 *       { label: 'Delete cache', danger: true, onClick: clear },
 *     ]}
 *   />
 *
 * Items with `hidden: true` are left out (use it for permission checks); when every item is
 * hidden the button is not rendered. Keyboard: arrow keys move, Enter runs, Escape closes.
 */
function MenuPanel({ items, close }) {
    const ref = useRef(null);

    useEffect(() => {
        const first = ref.current && ref.current.querySelector('[role="menuitem"]:not([disabled])');
        if (first) first.focus();
    }, []);

    const onKeyDown = (e) => {
        const list = Array.from(ref.current.querySelectorAll('[role="menuitem"]:not([disabled])'));
        const i = list.indexOf(document.activeElement);
        if (e.key === 'ArrowDown') { e.preventDefault(); (list[i + 1] || list[0]).focus(); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); (list[i - 1] || list[list.length - 1]).focus(); }
        else if (e.key === 'Home') { e.preventDefault(); list[0] && list[0].focus(); }
        else if (e.key === 'End') { e.preventDefault(); list[list.length - 1] && list[list.length - 1].focus(); }
        else if (e.key === 'Tab') { close(); }
    };

    return (
        <div ref={ref} role="menu" onKeyDown={onKeyDown} className="ds-action-menu">
            {items.map((item, idx) => {
                if (item.divider) return <div key={`d-${idx}`} className="ds-menu-divider" role="separator" />;
                if (item.heading) return <div key={`h-${idx}`} className="ds-menu-heading">{item.heading}</div>;
                return (
                    <button
                        key={item.key || item.label || idx}
                        type="button"
                        role="menuitem"
                        tabIndex={-1}
                        disabled={item.disabled}
                        title={item.title}
                        className={`ds-menu-item ${item.danger ? 'is-danger' : ''}`}
                        onClick={() => {
                            close();
                            if (item.onClick) item.onClick();
                        }}
                    >
                        {item.icon && <span className="ds-menu-item__icon"><Icon glyph={item.icon} size="sm" /></span>}
                        <span className="ds-menu-item__label">
                            <span className="ds-menu-item__title">{item.label}</span>
                            {item.description && <span className="ds-menu-item__desc">{item.description}</span>}
                        </span>
                        {item.badge !== undefined && item.badge !== null && item.badge !== '' && (
                            <span className="yelo-badge yelo-badge--accent yelo-badge--sm">{item.badge}</span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

export default function ActionMenu({
    label,
    icon,
    items = [],
    variant = 'secondary',
    size = 'md',
    align = 'end',
    width = 260,
    disabled = false,
    isLoading = false,
    iconOnly = false,
    className = '',
}) {
    const visible = items.filter((i) => i && !i.hidden);
    const actionable = visible.filter((i) => !i.divider && !i.heading);
    if (actionable.length === 0) return null;

    // Drop dividers left at the edges after hiding items
    const cleaned = visible.filter((item, i, arr) => !(item.divider && (i === 0 || i === arr.length - 1 || arr[i - 1].divider)));

    return (
        <Popover
            label={label}
            align={align}
            width={width}
            className={className}
            trigger={({ props, open }) => (
                <Button
                    {...props}
                    aria-haspopup="menu"
                    variant={variant}
                    size={size}
                    disabled={disabled}
                    isLoading={isLoading}
                    iconOnly={iconOnly}
                    aria-label={iconOnly ? label : undefined}
                    title={iconOnly ? label : undefined}
                    leadingIcon={icon ? <Icon glyph={icon} size="sm" /> : undefined}
                    trailingIcon={iconOnly ? undefined : <Icon glyph={open ? ICONS.chevronUp : ICONS.chevronDown} size="xs" />}
                >
                    {iconOnly ? null : label}
                </Button>
            )}
        >
            {({ close }) => <MenuPanel items={cleaned} close={close} />}
        </Popover>
    );
}
